import { Link } from 'react-router-dom';
import { Icon3D, type Icon3DName } from '../../components/Common';
import { ProductExplorer } from '../../components/ProductExplorer';
import {
  integrationGroups,
  onboardingMigration,
  platformMatrix,
  websiteScreenshotDescription,
} from '../../data/site';

export function OnboardingMigration() {
  return (
    <section className="section compact">
      <div className="container onboarding-migration">
        <Icon3D name="folder" size={88} className="migration-art" />
        <div>
          <div className="eyebrow">Included in onboarding</div>
          <h2>{onboardingMigration.title}</h2>
          <p>{onboardingMigration.description}</p>
        </div>
        <Link className="button secondary" to="/request-demo">
          Plan your move <span aria-hidden="true">↗</span>
        </Link>
      </div>
    </section>
  );
}

/** The offline-first story, told as the three-step round trip a record makes.
 * This is the product's central architectural claim, so it gets a diagram
 * rather than another card grid. */
export function OfflineFirst() {
  return (
    <section className="section">
      <div className="container grid two sync-section">
        <div>
          <div className="eyebrow">Offline-first</div>
          <h2>The connection is optional. The work is not.</h2>
          <p className="lead">
            Transport Book writes every record to a database on your own machine first. When the
            network comes back, changes sync to the shared database your organisation configures —
            and conflicting edits are surfaced for review rather than silently overwritten.
          </p>
          <p>
            A weak signal at a plant, a depot, or a highway stop never stops a driver being
            assigned, a job card being raised, or a POD being logged.
          </p>
          <div className="actions">
            <Link className="button secondary" to="/desktop">
              How the desktop app works
            </Link>
          </div>
        </div>
        <div className="sync-diagram motion-item" aria-hidden="true">
          <div className="sync-node sync-node--device">
            <strong>This device</strong>
            <span>Local database</span>
          </div>
          <div className="sync-track">
            <span className="sync-packet" />
            <span className="sync-packet sync-packet--b" />
          </div>
          <div className="sync-node sync-node--cloud">
            <strong>Your shared database</strong>
            <span>Team devices in step</span>
          </div>
        </div>
      </div>
    </section>
  );
}

export function ScreenshotGallery({
  limit,
  title = 'Take a closer look at your next workspace.',
  body = websiteScreenshotDescription,
  // Suppressed on the desktop page itself, where the link would point at the
  // page the reader is already on.
  link = true,
}: {
  limit?: number;
  title?: string;
  body?: string;
  link?: boolean;
} = {}) {
  return (
    <section className="section">
      <div className="container">
        <Head eyebrow="Inside the application" title={title} body={body} />
        <ProductExplorer limit={limit} />
        {link && (
          <div className="actions">
            <Link className="button secondary" to="/desktop">
              See the desktop workspace
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}

export function IntegrationStrip() {
  return (
    <section className="section surface">
      <div className="container">
        <Head
          eyebrow="Connected where it matters"
          title="It plugs into the services your fleet already pays for."
          body="Tracking, fuel cards, tolls, bank statements, document storage, and accounting — each optional, each off until you configure it."
        />
        <IntegrationMarquee items={integrationGroups.flatMap(group => group.items)} />
        <div className="actions">
          <Link className="button secondary" to="/integrations">
            See all integrations
          </Link>
        </div>
      </div>
    </section>
  );
}

/** A feature group's icon, by the name product metadata gives it. */
const featureIcons: Record<string, Icon3DName> = {
  route: 'file-text',
  wrench: 'tool',
  chart: 'rupee',
  pin: 'map-pin',
  fuel: 'card',
  offline: 'computer',
  sync: 'link',
};

/** The integrations as two rows drifting in opposite directions. Each row is
 * its items twice over, so translating the track by half loops seamlessly;
 * the copy is hidden from assistive technology, which reads one list. With
 * reduced motion the rows stand still and wrap (marketing.css). */
function IntegrationMarquee({ items }: { items: string[] }) {
  const half = Math.ceil(items.length / 2);
  const rows = [items.slice(0, half), items.slice(half)];
  return (
    <div className="marquee">
      {rows.map((row, index) => (
        <div className={`marquee-row${index ? ' marquee-row--reverse' : ''}`} key={index}>
          <ul className="marquee-track chip-row">
            {row.map(item => (
              <li className="chip" key={item}>
                {item}
              </li>
            ))}
          </ul>
          <ul className="marquee-track marquee-copy chip-row" aria-hidden="true">
            {row.map(item => (
              <li className="chip" key={item}>
                {item}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

export function FeatureIcon({ name, size }: { name: string; size?: number }) {
  return <Icon3D name={featureIcons[name] ?? 'chart'} size={size} />;
}

export function Head({ eyebrow, title, body }: { eyebrow: string; title: string; body?: string }) {
  return (
    <div className="section-head">
      <div className="eyebrow">{eyebrow}</div>
      <h2>{title}</h2>
      {body && <p className="lead">{body}</p>}
    </div>
  );
}

export function PlatformComparison() {
  return (
    <section className="section">
      <div className="container">
        <Head
          eyebrow="The right surface for the moment"
          title="Full control on desktop and in the browser. Focused visibility on mobile."
          body="The desktop and the web app are one workspace; they differ in where the records live and what happens when the network drops."
        />
        <div className="table-scroll">
          <table className="compare">
            <thead>
              <tr>
                <th>Capability</th>
                <th>Desktop app</th>
                <th>Web app</th>
                <th>Mobile companion</th>
              </tr>
            </thead>
            <tbody>
              {platformMatrix.map(([capability, desktop, web, mobile]) => (
                <tr key={capability}>
                  <td>{capability}</td>
                  <td>{desktop}</td>
                  <td>{web}</td>
                  <td>{mobile}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="actions">
          <Link className="button secondary" to="/desktop">
            Explore desktop
          </Link>
          <Link className="button secondary" to="/web">
            Explore the web app
          </Link>
          <Link className="button secondary" to="/mobile">
            Explore mobile
          </Link>
        </div>
      </div>
    </section>
  );
}
